/**
 * Profile Social Media Page
 * Connect and manage Instagram and YouTube links
 * Toggle connection state with consistent profile styling
 *
 * @author Varun Pandey
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Instagram, Youtube, ArrowLeft, BarChart3, Link2, ChevronLeft, ChevronRight, Check, X, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { cn } from '../../utils/cn';
import toast from 'react-hot-toast';
import { Sheet } from '../../components/ui/Sheet';
import { InstagramAuthCore } from '@/instagram/InstagramAuthCore';
import { useInstagramStore } from '@/stores/instagramStore';
import * as YouTube from '@/services/youtubeService';
import { useSocialMediaStore } from '@/stores/socialMediaStore';
import { API_ENDPOINTS } from '@/api/endpoints';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip } from 'recharts';
import { ProfileCompletionBlocksControl } from '@/components/profile/ProfileCompletionBlocksControl';
import { Loader } from '@/components/ui';

const ENGAGEMENT_FETCH_DELAY_MS = 6000;

const formatCompactNumber = (value: number) => {
  if (!Number.isFinite(value)) return '-';
  return Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
};

export const ProfileSocialMediaPage: React.FC = () => {
  const navigate = useNavigate();
  const instagramConnection = useInstagramStore((s) => s.connection);
  const {
    instagramEngagement,
    youtubeEngagement,
    ensureFetched,
    refreshInstagram,
    refreshYouTube,
  } = useSocialMediaStore();
  const [isConnectingInstagram, setIsConnectingInstagram] = useState(false);
  const [isConnectingYouTube, setIsConnectingYouTube] = useState(false);
  const [isYouTubeConnectOpen, setIsYouTubeConnectOpen] = useState(false);
  const [youtubeQuery, setYoutubeQuery] = useState('');
  const [youtubeChannels, setYoutubeChannels] = useState<YouTube.YouTubeChannel[]>([]);
  const [youtubeChannelPages, setYoutubeChannelPages] = useState<YouTube.YouTubeChannel[][]>([]);
  const [youtubeChannelPageTokens, setYoutubeChannelPageTokens] = useState<(string | null)[]>([]);
  const [youtubeChannelPage, setYoutubeChannelPage] = useState(1);
  const [youtubeSearchLoading, setYoutubeSearchLoading] = useState(false);
  const [selectedYouTubeChannel, setSelectedYouTubeChannel] = useState<YouTube.YouTubeChannel | null>(null);
  const [youtubeConfirmOpen, setYoutubeConfirmOpen] = useState(false);
  const [isRefreshingInstagramEngagement, setIsRefreshingInstagramEngagement] = useState(false);
  const instagramEngagementRefreshInFlightRef = useRef(false);

  const instagramConnectedFromApi =
    instagramEngagement?.data.instagram_connected === true;
  const instagramConnectedFromStore = instagramConnection?.connected === true;
  const instagramConnected = instagramConnectedFromApi || instagramConnectedFromStore;
  const youtubeConnected = youtubeEngagement?.success === true;

  // Fetch engagement via store when page loads (once per session)
  useEffect(() => {
    void ensureFetched();
  }, [ensureFetched]);

  // After Instagram OAuth connect, wait before fetching engagement stats.
  useEffect(() => {
    if (!instagramConnectedFromStore) return;
    if (instagramEngagement?.data.instagram_connected === true) return;
    if (instagramEngagementRefreshInFlightRef.current) return;

    instagramEngagementRefreshInFlightRef.current = true;
    let cancelled = false;

    const run = async () => {
      setIsRefreshingInstagramEngagement(true);
      try {
        await refreshInstagram({ delayMs: ENGAGEMENT_FETCH_DELAY_MS });
      } finally {
        instagramEngagementRefreshInFlightRef.current = false;
        if (!cancelled) setIsRefreshingInstagramEngagement(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [
    instagramConnectedFromStore,
    instagramEngagement?.data.instagram_connected,
    refreshInstagram,
  ]);

  const handleConnectInstagram = async () => {
    if (instagramConnected || isConnectingInstagram) return;
    setIsConnectingInstagram(true);
    try {
      // Start OAuth in "connect" context; after callback the app returns here.
      await InstagramAuthCore.startOAuth('connect', { returnPath: '/profile/social-media' });
      // After successful backend connect and redirect, store.ensureFetched()
      // will run again on mount or you can manually call refreshInstagram().
      void refreshInstagram();
    } catch (err) {
      console.error('Instagram connect error:', err);
      toast.error('Unable to start Instagram connect. Please try again.');
      setIsConnectingInstagram(false);
    }
  };

  const handleConnectYouTube = async () => {
    if (youtubeConnected || isConnectingYouTube) return;
    setIsYouTubeConnectOpen((prev) => !prev);
  };

  const handleSearchYoutube = async () => {
    if (!youtubeQuery.trim()) return;
    setYoutubeSearchLoading(true);
    setYoutubeChannels([]);
    setYoutubeChannelPages([]);
    setYoutubeChannelPageTokens([]);
    setYoutubeChannelPage(1);
    setSelectedYouTubeChannel(null);
    try {
      const { channels, nextPageToken } = await YouTube.searchChannels(youtubeQuery);
      setYoutubeChannels(channels);
      setYoutubeChannelPages([channels]);
      setYoutubeChannelPageTokens([nextPageToken ?? null]);
      setYoutubeChannelPage(1);
    } catch (err) {
      console.error('YouTube search failed:', err);
      toast.error('YouTube search failed. Check your API key.');
    } finally {
      setYoutubeSearchLoading(false);
    }
  };

  const handleYouTubeChannelsPrevPage = () => {
    if (youtubeChannelPage <= 1) return;
    const newPage = youtubeChannelPage - 1;
    setYoutubeChannelPage(newPage);
    const prevPageChannels = youtubeChannelPages[newPage - 1] ?? [];
    setYoutubeChannels(prevPageChannels);
  };

  const handleYouTubeChannelsNextPage = async () => {
    const currentIndex = youtubeChannelPage - 1;
    const nextToken = youtubeChannelPageTokens[currentIndex];
    if (!nextToken) return;

    if (youtubeChannelPages[currentIndex + 1]) {
      const newPage = youtubeChannelPage + 1;
      setYoutubeChannelPage(newPage);
      setYoutubeChannels(youtubeChannelPages[currentIndex + 1]);
      return;
    }

    setYoutubeSearchLoading(true);
    try {
      const { channels, nextPageToken } = await YouTube.searchChannels(
        youtubeQuery,
        nextToken || undefined,
      );
      setYoutubeChannelPages((prev) => [...prev, channels]);
      setYoutubeChannelPageTokens((prev) => [...prev, nextPageToken ?? null]);
      const newPage = youtubeChannelPage + 1;
      setYoutubeChannelPage(newPage);
      setYoutubeChannels(channels);
    } catch (err) {
      console.error('YouTube search failed:', err);
      toast.error('YouTube search failed. Please try again.');
    } finally {
      setYoutubeSearchLoading(false);
    }
  };

  const handleSelectYouTubeChannelForConnect = (channel: YouTube.YouTubeChannel) => {
    setSelectedYouTubeChannel(channel);
    setIsYouTubeConnectOpen(false);
    setYoutubeConfirmOpen(true);
  };

  const handleConfirmConnectYouTube = async () => {
    if (!selectedYouTubeChannel) return;
    setIsConnectingYouTube(true);
    try {
      await fetch(API_ENDPOINTS.SOCIAL.ARTIST_YOUTUBE_CONNECT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ channel_id: selectedYouTubeChannel.id }),
        credentials: 'include',
      });
      toast.success('YouTube channel connected');
      await refreshYouTube({ delayMs: ENGAGEMENT_FETCH_DELAY_MS });
      setYoutubeConfirmOpen(false);
      setSelectedYouTubeChannel(null);
      setIsYouTubeConnectOpen(false);
    } catch (err) {
      console.error('YouTube connect error:', err);
      toast.error('Unable to connect YouTube. Please try again.');
    } finally {
      setIsConnectingYouTube(false);
    }
  };

  return (
    <div className="w-full bg-[#0B0B0D] text-white">
      <div className="w-full mx-auto md:px-4 md:py-6 lg:py-8 pb-16">
        <header className="mb-6 lg:mb-8">
          <div className="mb-5 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex min-h-[44px] shrink-0 items-center gap-2 -ml-2 rounded-xl pl-2 pr-3 text-[#8E8E93] transition-colors hover:bg-[#1C1C1E] hover:text-white active:bg-[#2C2C2E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D]"
            >
              <ArrowLeft className="h-5 w-5 shrink-0" />
              <span className="text-sm font-medium">Back</span>
            </button>
            <ProfileCompletionBlocksControl />
          </div>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 lg:h-12 lg:w-12 shrink-0 items-center justify-center rounded-xl bg-[#1C1C1E] border border-[#3A3A3C]">
              <BarChart3 className="h-5 w-5 lg:h-6 lg:w-6 text-[#8E8E93]" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-white tracking-tight uppercase tracking-widest">
                Social media
              </h1>
              <p className="text-[#8E8E93] text-sm lg:text-base mt-0.5">
                Connect Instagram and YouTube to showcase real engagement
              </p>
            </div>
          </div>
        </header>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6 "
        >
          {/* Instagram */}
          <section className=" rounded-2xl border border-[#3A3A3C] p-5 lg:p-6 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] flex items-center justify-center">
                  <Instagram className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Instagram</p>
                  <p className="text-xs text-[#8E8E93]">
                    {instagramConnected ? 'Connected' : 'Connect to show Instagram stats'}
                  </p>
                </div>
              </div>
              {!instagramConnected && (
                <Button
                  type="button"
                  variant="ghost-primary"
                  className="px-3 py-2 text-xs lg:text-sm h-auto"
                  onClick={handleConnectInstagram}
                  disabled={isConnectingInstagram}
                  isLoading={isConnectingInstagram}
                >
                  <Link2 className="h-4 w-4" />
                  Connect
                </Button>
              )}
            </div>
            {isRefreshingInstagramEngagement && (
              <div className="flex items-center justify-center py-8">
                <Loader size="small" />
              </div>
            )}
            {!isRefreshingInstagramEngagement && instagramEngagement?.data.instagram_connected === true && instagramEngagement && (
              <>
                <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-3">
                  <div className="rounded-2xl bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)] border border-[#3A3A3C] px-4 py-3">
                    <p className="text-xs text-[#8E8E93]">Average likes</p>
                    <p className="text-xl font-semibold text-white mt-1">
                      {instagramEngagement.data.average_likes.toLocaleString()}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)] border border-[#3A3A3C] px-4 py-3">
                    <p className="text-xs text-[#8E8E93]">Average comments</p>
                    <p className="text-xl font-semibold text-white mt-1">
                      {instagramEngagement.data.average_comments.toLocaleString()}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)] border border-[#3A3A3C] px-4 py-3">
                    <p className="text-xs text-[#8E8E93]">Engagement rate</p>
                    <p className="text-xl font-semibold text-white mt-1">
                      {Math.round(instagramEngagement.data.engagement_rate)}%
                    </p>
                  </div>
                </div>
                <div className="grid gap-3 grid-cols-2 md:grid-cols-3 mt-3">
                  <div className="rounded-2xl bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)] border border-[#3A3A3C] px-4 py-3">
                    <p className="text-xs text-[#8E8E93]">Followers</p>
                    <p className="text-xl font-semibold text-white mt-1">
                      {instagramEngagement.data.followers_count.toLocaleString()}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)] border border-[#3A3A3C] px-4 py-3">
                    <p className="text-xs text-[#8E8E93]">Posts analysed</p>
                    <p className="text-xl font-semibold text-white mt-1">
                      {instagramEngagement.data.media_count.toLocaleString()}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)] border border-[#3A3A3C] px-4 py-3">
                    <p className="text-xs text-[#8E8E93]">Last updated</p>
                    <p className="text-xs font-medium text-white mt-1">
                      {instagramEngagement.data.calculated_at
                        ? new Date(instagramEngagement.data.calculated_at).toLocaleString()
                        : 'Just now'}
                    </p>
                  </div>
                </div>
                <div className="mt-4 rounded-2xl bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)] border border-[#3A3A3C] px-4 py-3">
                  <p className="text-xs text-[#8E8E93] mb-2">Last 7 days engagement</p>
                  <div className="h-40 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={(instagramEngagement.data.last_7_posts_engagement || []).map((value, index) => ({
                          label: `D${index + 1}`,
                          value,
                        }))}
                        margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                      >
                        <defs>
                          <linearGradient id="igEngagementGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#F33363" stopOpacity={0.4} />
                            <stop offset="100%" stopColor="#F33363" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#3A3A3C" vertical={false} />
                        <XAxis dataKey="label" stroke="#8E8E93" fontSize={11} tickLine={false} />
                        <YAxis stroke="#8E8E93" fontSize={11} tickLine={false} axisLine={false} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1C1C1E',
                            border: '1px solid #3A3A3C',
                            borderRadius: '12px',
                            padding: '8px 10px',
                            fontSize: '11px',
                            color: '#fff',
                          }}
                          cursor={{ stroke: '#3A3A3C' }}
                          formatter={(value: number) => [`${value?.toFixed?.(2) ?? value}%`, 'Engagement']}
                          labelFormatter={(label) => `Day ${label.replace('D', '')}`}
                        />
                        <Area
                          type="monotone"
                          dataKey="value"
                          stroke="#F33363"
                          fill="url(#igEngagementGradient)"
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            )}
          </section>

          {/* YouTube   */}
          <section className=" rounded-2xl border border-[#3A3A3C] p-5 lg:p-6 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-2xl bg-[#FF0000] flex items-center justify-center">
                  <Youtube className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">YouTube</p>
                  <p className="text-xs text-[#8E8E93]">
                    {youtubeConnected ? 'Connected' : 'Connect to show channel stats'}
                  </p>
                </div>
              </div>
              {!youtubeConnected && (
                <Button
                  type="button"
                  variant="ghost-primary"
                  className="px-3 py-2 text-xs lg:text-sm h-auto"
                  onClick={handleConnectYouTube}
                  disabled={isConnectingYouTube}
                >
                  <Link2 className="h-4 w-4" />
                  Connect
                </Button>
              )}
            </div>
            {!youtubeConnected && (
              <Sheet
                open={isYouTubeConnectOpen}
                onOpenChange={setIsYouTubeConnectOpen}
                title="Search YouTube"
                description="Search your YouTube channel, then confirm to connect it to your profile."
                preventOutsideClose={isConnectingYouTube}
                preventEscapeClose={isConnectingYouTube}
              >
                <div className="rounded-2xl space-y-4">
                  <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                    <Input
                      placeholder="Search YouTube channels"
                      value={youtubeQuery}
                      onChange={(e) => setYoutubeQuery(e.target.value)}
                      className="sm:flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void handleSearchYoutube();
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      className="w-full sm:w-auto px-4 py-2 text-xs lg:text-sm border border-[#3A3A3C]"
                      onClick={handleSearchYoutube}
                      disabled={youtubeSearchLoading}
                      isLoading={youtubeSearchLoading}
                    >
                      Search
                    </Button>
                  </div>

                  {youtubeChannels.length > 0 && (
                    <div className="mt-3 space-y-3 overflow-y-auto custom-scrollbar">
                      <p className="text-[11px] text-[#8E8E93]">Select a channel to connect:</p>
                      <div className="grid grid-cols-1 md:grid-cols-1 gap-3">
                        {youtubeChannels.map((ch) => {
                          const thumbUrl = YouTube.getYouTubeThumbUrl(ch.thumbnails);
                          const isSelected = selectedYouTubeChannel?.id === ch.id;
                          return (
                            <button
                              key={ch.id}
                              type="button"
                              onClick={() => handleSelectYouTubeChannelForConnect(ch)}
                              className={cn(
                                'w-full flex items-center gap-3 rounded-xl px-3 py-2 text-left bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)] border transition-colors disabled:opacity-70 disabled:cursor-not-allowed',
                                isSelected ? 'border-[#F33363]' : 'border-[#3A3A3C] hover:border-[#4A4A4C]',
                              )}
                              disabled={youtubeSearchLoading || isConnectingYouTube}
                            >
                              <div className="relative h-10 w-10 rounded-full bg-[#1C1C1E] border border-[#3A3A3C] overflow-hidden shrink-0">
                                {thumbUrl && (
                                  <img
                                    src={thumbUrl}
                                    alt={ch.title}
                                    className="h-full w-full object-cover"
                                    loading="lazy"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                    }}
                                  />
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-xs text-[#E5E5EA] truncate">{ch.title}</p>
                              </div>
                              <span
                                className={cn(
                                  'inline-flex items-center justify-center h-5 w-5 rounded-md border-2',
                                  isSelected
                                    ? 'border-[#F33363] bg-[#F33363] text-white'
                                    : 'border-[#3A3A3C] bg-transparent text-transparent',
                                )}
                              >
                                <Check className="h-3 w-3" />
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      <div className="flex items-center justify-between pt-1">
                        <button
                          type="button"
                          onClick={handleYouTubeChannelsPrevPage}
                          disabled={youtubeChannelPage <= 1 || youtubeSearchLoading}
                          className={cn(
                            'flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-medium border transition-colors min-w-[4.5rem]',
                            youtubeChannelPage <= 1 || youtubeSearchLoading
                              ? 'border-[#3A3A3C] text-[#8E8E93] cursor-not-allowed opacity-60'
                              : 'border-[#3A3A3C] bg-[#1C1C1E] text-white hover:border-[#F33363]/50 hover:bg-[#252527]',
                          )}
                        >
                          <ChevronLeft className="h-3 w-3" />
                          Prev
                        </button>
                        <span className="text-[11px] text-[#8E8E93]">
                          Page {youtubeChannelPage}
                        </span>
                        <button
                          type="button"
                          onClick={handleYouTubeChannelsNextPage}
                          disabled={
                            !youtubeChannelPageTokens[youtubeChannelPage - 1] || youtubeSearchLoading
                          }
                          className={cn(
                            'flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-medium border transition-colors min-w-[4.5rem]',
                            !youtubeChannelPageTokens[youtubeChannelPage - 1] || youtubeSearchLoading
                              ? 'border-[#3A3A3C] text-[#8E8E93] cursor-not-allowed opacity-60'
                              : 'border-[#3A3A3C] bg-[#1C1C1E] text-white hover:border-[#F33363]/50 hover:bg-[#252527]',
                          )}
                        >
                          Next
                          <ChevronRight className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </Sheet>
            )}

            {youtubeConnected && youtubeEngagement && (
              <>
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="rounded-2xl bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)] border border-[#3A3A3C] px-4 py-3">
                    <p className="text-xs text-[#8E8E93]">Subscribers</p>
                    <p className="text-xl font-semibold text-white mt-1">
                      {formatCompactNumber(youtubeEngagement.data.subscriber_count)}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)] border border-[#3A3A3C] px-4 py-3">
                    <p className="text-xs text-[#8E8E93]">Average views</p>
                    <p className="text-xl font-semibold text-white mt-1">
                      {formatCompactNumber(youtubeEngagement.data.average_views)}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)] border border-[#3A3A3C] px-4 py-3">
                    <p className="text-xs text-[#8E8E93]">Average likes</p>
                    <p className="text-xl font-semibold text-white mt-1">
                      {formatCompactNumber(youtubeEngagement.data.average_likes)}
                    </p>
                  </div>
                </div>
                <div className="mt-4 rounded-2xl bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)] border border-[#3A3A3C] px-4 py-3">
                  <p className="text-xs text-[#8E8E93] mb-2">Last 7 days engagement</p>
                  <div className="h-40 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={(youtubeEngagement.data.last_7_videos_engagement || []).map((value, index) => ({
                          label: `D${index + 1}`,
                          value,
                        }))}
                        margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                      >
                        <defs>
                          <linearGradient id="ytEngagementGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#FF0000" stopOpacity={0.4} />
                            <stop offset="100%" stopColor="#FF0000" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#3A3A3C" vertical={false} />
                        <XAxis dataKey="label" stroke="#8E8E93" fontSize={11} tickLine={false} />
                        <YAxis stroke="#8E8E93" fontSize={11} tickLine={false} axisLine={false} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1C1C1E',
                            border: '1px solid #3A3A3C',
                            borderRadius: '12px',
                            padding: '8px 10px',
                            fontSize: '11px',
                            color: '#fff',
                          }}
                          cursor={{ stroke: '#3A3A3C' }}
                          formatter={(value: number) => [`${value?.toFixed?.(2) ?? value}%`, 'Engagement']}
                          labelFormatter={(label) => `Day ${label.replace('D', '')}`}
                        />
                        <Area
                          type="monotone"
                          dataKey="value"
                          stroke="#FF0000"
                          fill="url(#ytEngagementGradient)"
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            )}
          </section>
        </motion.div>
      </div>

      <AnimatePresence>
        {youtubeConfirmOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                if (isConnectingYouTube) return;
                setYoutubeConfirmOpen(false);
                setSelectedYouTubeChannel(null);
                setIsYouTubeConnectOpen(true);
              }}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-sm rounded-[2.5rem] p-8 shadow-2xl bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)] border border-[#3A3A3C]/50"
            >
              <div className="w-16 h-16 rounded-full bg-[#F33363]/10 flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="h-8 w-8 text-[#F33363]" />
              </div>
              <h3 className="text-xl font-black text-center text-white tracking-tight mb-2">Confirm channel?</h3>
              <p className="text-[#8E8E93] text-center text-sm mb-6">
                {selectedYouTubeChannel
                  ? `Add "${selectedYouTubeChannel.title}" to your profile.`
                  : 'Select a channel to continue.'}
              </p>
              <div className="flex flex-col gap-3">
                <Button
                  type="button"
                  variant="active"
                  className="w-full py-4 rounded-2xl font-bold bg-[#F33363] hover:bg-[#FF5E8E] border-none"
                  onClick={handleConfirmConnectYouTube}
                  disabled={!selectedYouTubeChannel || isConnectingYouTube}
                  isLoading={isConnectingYouTube}
                >
                  Yes, add this channel
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    if (isConnectingYouTube) return;
                    setYoutubeConfirmOpen(false);
                    setSelectedYouTubeChannel(null);
                    setIsYouTubeConnectOpen(true);
                  }}
                  className="w-full py-4 text-[#8E8E93] font-bold hover:text-white transition-colors"
                  disabled={isConnectingYouTube}
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

